import React, { useEffect, useState } from 'react';
import { Suspense } from 'react';
import dynamic from 'next/dynamic';
import { lazily } from 'react-lazily';
import { FormNavigation } from '../../components/form-navigation';
import { useRouter } from 'next/router';
import { useTranslation, Trans } from 'react-i18next';

import styles from './signin-form.module.scss';
import { useForm } from 'react-hook-form';
import { postSignin, PostErrorResponse, PostSigninRequest } from 'services/idm';
import { PUBLIC_BASE_URL_ENV } from 'tools/environments';
import { useAppTranslation } from 'hooks/use-translates';
import { useCaseInsensitiveParam } from 'hooks/use-case-insensitive-params';
import { apiErrorHandler } from '@login/helpers';

const Button = dynamic(() => import('core/generic/elements/button'), {
    ssr: false,
});
const Loading = dynamic(() => import('core/generic/elements/loading'), {
    ssr: false,
});
const InfoBlock = dynamic(() => import('core/generic/elements/info-block'), {
    ssr: false,
});
const { Insert } = lazily(() => import('core/generic/elements/form'));

type SigninFormPlaceholders = {
    registerUrl?: string;
    signInUrl?: string;
    forgottenPasswordUrl?: string;
};
type SigninFormProps = {
    placeholders: SigninFormPlaceholders | null;
    token?: string | null;
    userUnderAge?: boolean;
};

export const SigninForm = ({
    placeholders,
    token,
    userUnderAge,
}: SigninFormProps) => {
    const { i18n } = useTranslation();
    const { push } = useRouter();
    const [apiErrorMessage, setApiErrorMessage] = useState('');
    const [continueAsName, setContinueAsName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { t } = useAppTranslation(placeholders);
    const returnUrl = useCaseInsensitiveParam('returnUrl');
    const {
        register,
        handleSubmit,
        setValue,
        setError,
        formState: { errors },
    } = useForm<PostSigninRequest>({ mode: 'onBlur' });

    useEffect(() => {
        if (userUnderAge) setApiErrorMessage(t('signin.user_under_age'));
    }, [userUnderAge]);

    const handleOnSubmit = async (formData: { username: string; password: string }) => {
        try {
            setApiErrorMessage('');
            setIsSubmitting(true);
            const response = await postSignin(
                {
                    username: formData.username.trim(),
                    password: formData.password,
                    returnUrl: returnUrl,
                    loginType: 1,
                },
                i18n.language,
                token,
            );

            if (response.action === 'Redirect' && response.redirectUrl) {
                if (response.redirectUrl.startsWith('/connect')) {
                    const absoluteUrl = `${window.location.origin}${response.redirectUrl}`;
                    window.location.href = absoluteUrl;
                } else {
                    push(response.redirectUrl);
                }
            } else {
                setIsSubmitting(false);
                if (
                    response.action === 'Display' &&
                    response.flow.flowId === 'signin' &&
                    response.flow.contentId === 'user-under-age'
                ) {
                    setApiErrorMessage(t('signin.user_under_age'));
                }
            }
        } catch (e) {
            const error = e as PostErrorResponse;
            apiErrorHandler(error, i18n.language);
            setIsSubmitting(false);
            setValue('password', '');
            if (error.title === 'INVALID_LOGIN') {
                setApiErrorMessage(t('signin.invalid_credentials_error_ec4'));
            } else if (error.title === 'USER_ALREADY_SIGNED_IN') {
                setApiErrorMessage(
                    t('signin.user_already_signed_in_error', {
                        name: (error as PostErrorResponse).detail,
                    }),
                );
                setContinueAsName(error.detail);
            }
            if (error.status === 422 && error.title === 'VALIDATION_ERROR') {
                if (error.errors?.Username) {
                    setError('username', { message: error.errors?.Username[0] });
                }
                if (error.errors?.Password) {
                    setError('password', { message: error.errors?.Password[0] });
                }
            }
        }
    };

    return (
        <Suspense fallback={<Loading />}>
            <form
                onSubmit={handleSubmit(handleOnSubmit)}
                id="form-profile"
                className={styles['signin-form']}
            >
                <FormNavigation
                    activeBtn="signin"
                    signInUrl={placeholders?.signInUrl}
                    registerUrl={placeholders?.registerUrl}
					returnUrl={returnUrl}
                />
                {apiErrorMessage && (
                    <InfoBlock variant="error">
                        <div className={styles['error-info-block']}>
                            <p className="body-small">
                                <Trans
                                    i18nKey={apiErrorMessage}
                                    components={[
                                        <Button
                                            variant="link"
                                            dimension="small"
                                            className={styles['forgot-password']}
                                        />,
                                    ]}
                                />
                            </p>
                        </div>
                    </InfoBlock>
                )}
                {!continueAsName && (
                    <>
                        <div className={styles['input-wrapper']}>
                            <Insert
                                {...register('username', {
                                    required: t('signin.username_required'),
                                })}
                                inputMode="email"
                                defaultValue={''}
                                tabIndex={0}
                                id="username"
                                label={t('signin.username_placeholder')}
                                required={false}
                                error={errors.username?.message}
                                errorIcon="AlertCircleFilled"
                                disabled={false}
                            />
                            <Insert
                                {...register('password', {
                                    required: t('signin.password_required'),
                                })}
                                defaultValue={''}
                                tabIndex={0}
                                id="password"
                                label={t('signin.password_label')}
                                required={false}
                                error={errors.password?.message}
                                errorIcon="AlertCircleFilled"
                                disabled={false}
                                type="password"
                            />
                        </div>
                        <Button
                            type="button"
                            href={placeholders?.forgottenPasswordUrl || '/forgot-password'}
                            label={`${t('signin.forgot_password_link')}`}
                            variant="link"
                            className="cta-underlined-medium"
                        />
                        <Button
                            type="submit"
                            label={t('signin.signin_button')}
                            isLoading={isSubmitting}
                            disabled={isSubmitting}
                            variant="primary"
                            dimension="large"
                            fullWidth={true}
                        />
                    </>
                )}
                {continueAsName && (
                    <>
                        <Button
                            type="button"
                            label={t('signin.continue_as_button', {
                                name: continueAsName,
                            })}
                            variant="primary"
                            href={returnUrl || PUBLIC_BASE_URL_ENV}
                            fullWidth={true}
                        />
                        <Button
                            type="button"
                            label={t('signout.signout_button')}
                            variant="primary"
                            href="/signout"
                            fullWidth={true}
                        />
                    </>
                )}

                <div className={`body-small medium ${styles.registerText}`}>
                    <Trans
                        i18nKey={t('common.register_link')}
                        components={[
                            <Button
                                type="button"
                                variant="link"
                                className={styles['register-now']}
                            />,
                        ]}
                    />
                </div>
            </form>
        </Suspense>
    );
};

export default SigninForm;
